import React, {
  FC, ReactNode, useCallback, useEffect, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { ModalName, useModalsContext, useToggleModalsContext } from '@/common/contexts/globalModalContext';
import { ResponsiveImageUrls } from '@/types';
import ModalLayout from '@/common/components/Modal/ModalLayout/ModalLayout';
import ResponsiveImage from '@/common/components/ResponsiveImage/ResponsiveImage';
import styles from './CareersPopup.module.scss';

interface CareersPopupProps {
  title: string;
  backgroundImage: ResponsiveImageUrls | null;
  popupKey?: string;
  children: ReactNode;
}

const CareersPopup: FC<CareersPopupProps> = ({
  title, backgroundImage, children, popupKey,
}) => {
  const toggleModal = useToggleModalsContext();
  const { modalsState, properties } = useModalsContext();
  const isShown = modalsState[ModalName.careers] && popupKey === properties.popupKey;

  const closeHandler = useCallback((): void => {
    toggleModal({ [ModalName.careers]: false });
  }, [toggleModal]);

  const contentRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScrollState] = useState<boolean>(false);

  const checkIfHasScroll = useCallback(() => {
    if (!isShown) {
      return;
    }

    const containerElement = contentRef?.current?.parentElement;
    if (!containerElement) {
      return;
    }

    const isContentHigherThanContainer = containerElement.scrollHeight > containerElement.clientHeight;
    setHasScrollState(isContentHigherThanContainer);
  }, [isShown]);

  useEffect(() => {
    window.addEventListener('resize', checkIfHasScroll);
    checkIfHasScroll();

    return (): void => {
      window.removeEventListener('resize', checkIfHasScroll);
    };
  }, [isShown]);
  // Use isShown as dependency since if we're using this with no arguments checkIfHasScroll called only first time ↓
  // when popup is not in DOM
  // TODO review popups logic in order not to call render of popup if it's not supposed to be shown

  if (!isShown) {
    return null;
  }

  return (
    <ModalLayout
      closeHandler={closeHandler}
      customClassNames={{
        title: classNames(styles.title, hasScroll && styles.titleNonTransparent),
        container: styles.container,
      }}
      modalWindowTitle={title}
    >
      <div ref={contentRef} className={styles.contentWrapper}>
        {children}
      </div>
      { !hasScroll && !!backgroundImage && !!Object.keys(backgroundImage).length && (
        <ResponsiveImage
          className={styles.backgroundImage}
          urls={backgroundImage}
        />
      ) }
    </ModalLayout>
  );
};
CareersPopup.displayName = 'CareersPopup';

export default CareersPopup;
